import { Directive, ElementRef, Renderer2, Input, AfterViewInit, HostListener, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';

@Directive({
    // tslint:disable-next-line:directive-selector
    selector: '[data-toggle=tooltip],data-toggle',
    standalone: false
})
export class ToolTipDirective implements AfterViewInit, OnDestroy, OnChanges {
    // tslint:disable-next-line:no-input-rename
    @Input('data-toggle') value: string;
    // tslint:disable-next-line:no-input-rename
    @Input('title') title: string;
    @Input() container: string;
    @Input() placement: string;
    @Input() showAlways = false;
    
    private tooltipElement: HTMLElement | null = null;

    constructor(private readonly el: ElementRef, private readonly renderer: Renderer2) {}

    ngOnChanges(changes: SimpleChanges) {
        if (this.showAlways) {
            this.mouseenter();
        } else {
            this.mouseleave();
        }
    }

    ngAfterViewInit() {
        // Initialize tooltip attributes
        this.renderer.setAttribute(this.el.nativeElement, 'data-tooltip', this.title || '');
        this.renderer.setAttribute(this.el.nativeElement, 'data-placement', this.placement || 'auto');
    }

    @HostListener('mouseenter')
    mouseenter() {
        this.createTooltip();
        this.showTooltip();
    }

    @HostListener('mouseleave')
    mouseleave() {
        if (!this.showAlways) {
            this.hideTooltip();
        }
    }

    ngOnDestroy() {
        this.destroyTooltip();
    }

    private createTooltip() {
        // Destroy any existing tooltip
        this.destroyTooltip();
        
        // Create new tooltip element
        this.tooltipElement = this.renderer.createElement('div');
        
        // Add classes and styles
        this.renderer.addClass(this.tooltipElement, 'custom-tooltip');
        this.renderer.setStyle(this.tooltipElement, 'position', 'absolute');
        this.renderer.setStyle(this.tooltipElement, 'background', 'rgba(0, 0, 0, 0.8)');
        this.renderer.setStyle(this.tooltipElement, 'color', 'white');
        this.renderer.setStyle(this.tooltipElement, 'padding', '5px 10px');
        this.renderer.setStyle(this.tooltipElement, 'border-radius', '4px');
        this.renderer.setStyle(this.tooltipElement, 'font-size', '12px');
        this.renderer.setStyle(this.tooltipElement, 'z-index', '9999');
        this.renderer.setStyle(this.tooltipElement, 'pointer-events', 'none');
        
        // Set tooltip content
        this.renderer.setProperty(this.tooltipElement, 'innerHTML', this.title || '');
        
        // Determine container
        const containerElement = this.container ? 
            document.querySelector(this.container) : 
            document.body;
        
        // Add tooltip to container
        if (containerElement) {
            this.renderer.appendChild(containerElement, this.tooltipElement);
        }
    }

    private showTooltip() {
        if (!this.tooltipElement) return;
        
        // Make tooltip visible
        this.renderer.setStyle(this.tooltipElement, 'display', 'block');
        
        // Get host element position
        const hostRect = this.el.nativeElement.getBoundingClientRect();
        const tooltipRect = this.tooltipElement.getBoundingClientRect();
        
        // Calculate position based on placement
        let top = 0;
        let left = 0;
        
        const placement = this.placement || 'auto';
        
        switch (placement) {
            case 'top':
                top = hostRect.top - tooltipRect.height - 5;
                left = hostRect.left + (hostRect.width - tooltipRect.width) / 2;
                break;
            case 'bottom':
                top = hostRect.bottom + 5;
                left = hostRect.left + (hostRect.width - tooltipRect.width) / 2;
                break;
            case 'left':
                top = hostRect.top + (hostRect.height - tooltipRect.height) / 2;
                left = hostRect.left - tooltipRect.width - 5;
                break;
            case 'right':
                top = hostRect.top + (hostRect.height - tooltipRect.height) / 2;
                left = hostRect.right + 5;
                break;
            default: // auto placement
                // Default to bottom
                top = hostRect.bottom + 5;
                left = hostRect.left + (hostRect.width - tooltipRect.width) / 2;
                
                // Check if tooltip would go off-screen and adjust accordingly
                if (top + tooltipRect.height > window.innerHeight) {
                    top = hostRect.top - tooltipRect.height - 5;
                }
                
                if (left < 0) {
                    left = 5;
                } else if (left + tooltipRect.width > window.innerWidth) {
                    left = window.innerWidth - tooltipRect.width - 5;
                }
                break;
        }
        
        // Account for scroll position
        top += window.scrollY;
        left += window.scrollX;
        
        // Set position
        this.renderer.setStyle(this.tooltipElement, 'top', `${top}px`);
        this.renderer.setStyle(this.tooltipElement, 'left', `${left}px`);
    }

    private hideTooltip() {
        if (this.tooltipElement) {
            this.renderer.setStyle(this.tooltipElement, 'display', 'none');
        }
    }

    private destroyTooltip() {
        if (this.tooltipElement) {
            const parent = this.tooltipElement.parentNode;
            if (parent) {
                this.renderer.removeChild(parent, this.tooltipElement);
            }
            this.tooltipElement = null;
        }
    }
}